import { applySnapshot, types } from "mobx-state-tree";
import { kAnalyzerUserParams, kExemplarUserParams } from "../../../shared/shared";
export const ClassUserModel = types
    .model("ClassUser", {
    type: types.enumeration("UserType", ["teacher", "student"]),
    id: types.identifier,
    firstName: types.string,
    lastName: types.string,
    fullName: types.string,
    initials: types.string,
})
    .views((self) => {
    return {
        get displayName() {
            return self.type === "teacher"
                ? `Teacher ${self.lastName}`
                : self.fullName;
        }
    };
});
export const ClassModel = types
    .model("Class", {
    name: types.string,
    classHash: types.string,
    users: types.map(ClassUserModel),
    timestamp: types.optional(types.number, () => Date.now()),
})
    .actions((self) => {
    return {
        updateFromPortal(classInfo, includeAIUser) {
            const usersSnapshot = {};
            const users = [...classInfo.teachers, ...classInfo.students];
            users.forEach((user) => {
                usersSnapshot[user.id] = user;
            });
            // Add the fake exemplar user
            usersSnapshot[kExemplarUserParams.id] = kExemplarUserParams;
            // Add AI user if the unit has AI analysis configured
            if (includeAIUser) {
                usersSnapshot[kAnalyzerUserParams.id] = kAnalyzerUserParams;
            }
            // applySnapshot is used so the same user objects are updated
            applySnapshot(self.users, usersSnapshot);
            self.name = classInfo.name;
            self.classHash = classInfo.classHash;
            self.timestamp = classInfo.serverTimestamp || classInfo.localTimestamp;
        },
    };
})
    .views((self) => {
    return {
        getUserById(uid) {
            return self.users.get(uid);
        },
        get students() {
            return Array.from(self.users.values()).filter(u => u.type === "student");
        }
    };
})
    .views((self) => {
    return {
        // RESEARCHER-ACCESS: we use this to gate access to certain features
        // that are only available to teachers but may need to be available to
        // researchers as well.  How do we want to handle this since the
        // researcher is not in the class list?
        isTeacher(uid) {
            const user = self.getUserById(uid);
            return (user === null || user === void 0 ? void 0 : user.type) === "teacher";
        }
    };
});
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY2xhc3MuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvbW9kZWxzL3N0b3Jlcy9jbGFzcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQSxPQUFPLEVBQUUsYUFBYSxFQUF3QixLQUFLLEVBQUUsTUFBTSxpQkFBaUIsQ0FBQztBQUU3RSxPQUFPLEVBQUUsbUJBQW1CLEVBQUUsbUJBQW1CLEVBQUUsTUFBTSx3QkFBd0IsQ0FBQztBQUVsRixNQUFNLENBQUMsTUFBTSxjQUFjLEdBQUcsS0FBSztLQUNoQyxLQUFLLENBQUMsV0FBVyxFQUFFO0lBQ2xCLElBQUksRUFBRSxLQUFLLENBQUMsV0FBVyxDQUFDLFVBQVUsRUFBRSxDQUFDLFNBQVMsRUFBRSxTQUFTLENBQUMsQ0FBQztJQUMzRCxFQUFFLEVBQUUsS0FBSyxDQUFDLFVBQVU7SUFDcEIsU0FBUyxFQUFFLEtBQUssQ0FBQyxNQUFNO0lBQ3ZCLFFBQVEsRUFBRSxLQUFLLENBQUMsTUFBTTtJQUN0QixRQUFRLEVBQUUsS0FBSyxDQUFDLE1BQU07SUFDdEIsUUFBUSxFQUFFLEtBQUssQ0FBQyxNQUFNO0NBQ3ZCLENBQUM7S0FDRCxLQUFLLENBQUMsQ0FBQyxJQUFJLEVBQUUsRUFBRTtJQUNkLE9BQU87UUFDTCxJQUFJLFdBQVc7WUFDYixPQUFPLElBQUksQ0FBQyxJQUFJLEtBQUssU0FBUztnQkFDNUIsQ0FBQyxDQUFDLFdBQVcsSUFBSSxDQUFDLFFBQVEsRUFBRTtnQkFDNUIsQ0FBQyxDQUFDLElBQUksQ0FBQyxRQUFRLENBQUM7UUFDcEIsQ0FBQztLQUNGLENBQUM7QUFDSixDQUFDLENBQUMsQ0FBQztBQUlMLE1BQU0sQ0FBQyxNQUFNLFVBQVUsR0FBRyxLQUFLO0tBQzVCLEtBQUssQ0FBQyxPQUFPLEVBQUU7SUFDZCxJQUFJLEVBQUUsS0FBSyxDQUFDLE1BQU07SUFDbEIsU0FBUyxFQUFFLEtBQUssQ0FBQyxNQUFNO0lBQ3ZCLEtBQUssRUFBRSxLQUFLLENBQUMsR0FBRyxDQUFDLGNBQWMsQ0FBQztJQUNoQyxTQUFTLEVBQUUsS0FBSyxDQUFDLFFBQVEsQ0FBQyxLQUFLLENBQUMsTUFBTSxFQUFFLEdBQUcsRUFBRSxDQUFDLElBQUksQ0FBQyxHQUFHLEVBQUUsQ0FBQztDQUMxRCxDQUFDO0tBQ0QsT0FBTyxDQUFDLENBQUMsSUFBSSxFQUFFLEVBQUU7SUFDaEIsT0FBTztRQUNMLGdCQUFnQixDQUFDLFNBQW9CLEVBQUUsYUFBc0I7WUFDM0QsTUFBTSxhQUFhLEdBQWtDLEVBQ3BELENBQUM7WUFDRixNQUFNLEtBQUssR0FBRyxDQUFDLEdBQUcsU0FBUyxDQUFDLFFBQVEsRUFBRSxHQUFHLFNBQVMsQ0FBQyxRQUFRLENBQUMsQ0FBQztZQUM3RCxLQUFLLENBQUMsT0FBTyxDQUFDLENBQUMsSUFBSSxFQUFFLEVBQUU7Z0JBQ3JCLGFBQWEsQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDLEdBQUcsSUFBSSxDQUFDO1lBQ2hDLENBQUMsQ0FBQyxDQUFDO1lBRUgsNkJBQTZCO1lBQzdCLGFBQWEsQ0FBQyxtQkFBbUIsQ0FBQyxFQUFFLENBQUMsR0FBRyxtQkFBbUIsQ0FBQztZQUU1RCxxREFBcUQ7WUFDckQsSUFBSSxhQUFhLEVBQUUsQ0FBQztnQkFDbEIsYUFBYSxDQUFDLG1CQUFtQixDQUFDLEVBQUUsQ0FBQyxHQUFHLG1CQUFtQixDQUFDO1lBQzlELENBQUM7WUFFRCw2REFBNkQ7WUFDN0QsYUFBYSxDQUFDLElBQUksQ0FBQyxLQUFLLEVBQUUsYUFBYSxDQUFDLENBQUM7WUFDekMsSUFBSSxDQUFDLElBQUksR0FBRyxTQUFTLENBQUMsSUFBSSxDQUFDO1lBQzNCLElBQUksQ0FBQyxTQUFTLEdBQUcsU0FBUyxDQUFDLFNBQVMsQ0FBQztZQUNyQyxJQUFJLENBQUMsU0FBUyxHQUFHLFNBQVMsQ0FBQyxlQUFlLElBQUksU0FBUyxDQUFDLGNBQWMsQ0FBQztRQUN6RSxDQUFDO0tBQ0YsQ0FBQztBQUNKLENBQUMsQ0FBQztLQUNELEtBQUssQ0FBQyxDQUFDLElBQUksRUFBRSxFQUFFO0lBQ2QsT0FBTztRQUNMLFdBQVcsQ0FBQyxHQUFXO1lBQ3JCLE9BQU8sSUFBSSxDQUFDLEtBQUssQ0FBQyxHQUFHLENBQUMsR0FBRyxDQUFDLENBQUM7UUFDN0IsQ0FBQztRQUNELElBQUksUUFBUTtZQUNWLE9BQU8sS0FBSyxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLE1BQU0sRUFBRSxDQUFDLENBQUMsTUFBTSxDQUFDLENBQUMsQ0FBQSxFQUFFLENBQUEsQ0FBQyxDQUFDLElBQUksS0FBSyxTQUFTLENBQUMsQ0FBQztRQUN6RSxDQUFDO0tBQ0YsQ0FBQztBQUNKLENBQUMsQ0FBQztLQUNELEtBQUssQ0FBQyxDQUFDLElBQUksRUFBRSxFQUFFO0lBQ2QsT0FBTztRQUNMLG9FQUFvRTtRQUNwRSxzRUFBc0U7UUFDdEUsZ0VBQWdFO1FBQ2hFLHVDQUF1QztRQUN2QyxTQUFTLENBQUMsR0FBVztZQUNuQixNQUFNLElBQUksR0FBSSxJQUFJLENBQUMsV0FBVyxDQUFDLEdBQUcsQ0FBQyxDQUFDO1lBQ3BDLE9BQU8sQ0FBQSxJQUFJLGFBQUosSUFBSSx1QkFBSixJQUFJLENBQUUsSUFBSSxNQUFLLFNBQVMsQ0FBQztRQUNsQyxDQUFDO0tBQ0YsQ0FBQztBQUNKLENBQUMsQ0FBQyxDQUFDIn0=